<script setup>
import { Link } from '@inertiajs/vue3';
/*
 * Er tilbuddet aegte?
 *
 * BEVISET STAAR VED SIDEN AF PAASTANDEN. Vi skriver ikke "butikken
 * lyver"; vi viser hvad butikken paastaar, hvad vi selv har maalt, og
 * hvor mange dage vores maaling daekker. Laeseren kan selv regne.
 *
 * Er daekningen kortere end referenceperioden, siger vi det. En
 * vurdering paa fire dages historik er ikke en vurdering.
 */
import { computed } from 'vue';

const props = defineProps({
    data: { type: Object, required: true },
});

const varer = computed(() => props.data.varer || []);

function kr(v) {
    const n = Number(v);
    if (isNaN(n)) return '';
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(n);
}

function navn(v) {
    return v.display_name || v.model_short_name || 'Vare';
}

function billede(v) {
    return v.images?.length ? `/images/${v.images[0].id}` : null;
}

function badge(status) {
    if (status === 'verified') return 'tag tag--god';
    if (status === 'weak') return 'tag tag--svag';
    if (status === 'inflated') return 'tag tag--daarlig';
    return 'tag';
}

// Besparelsen i procent, regnet mod dét vi selv har maalt — ikke mod
// butikkens paastand.
function procent(t) {
    if (!t.thirty_day_low || !t.price) return null;
    const p = Math.round((1 - Number(t.price) / Number(t.thirty_day_low)) * 100);
    return p > 0 ? p : null;
}
</script>

<template>
    <p v-if="!varer.length" class="tom">Ingen tilbud matcher lige nu.</p>

    <div v-else class="tilbudsgitter">
        <article v-for="(v, i) in varer" :key="v.pricecompare_id || i" class="tilbud">
            <Link v-if="v.route_to_productdetail" :href="v.route_to_productdetail" class="tilbud__billede">
                <picture v-if="billede(v)">
                    <source :srcset="`${billede(v)}?w=320&fmt=webp`" type="image/webp" />
                    <img :src="`${billede(v)}?w=320`" :alt="navn(v)" width="160" height="160" loading="lazy" decoding="async" />
                </picture>
                <span v-else class="tilbud__intetbillede" aria-hidden="true"></span>
            </Link>

            <div class="tilbud__krop">
                <h3 class="tilbud__navn">
                    <Link v-if="v.route_to_productdetail" :href="v.route_to_productdetail">{{ navn(v) }}</Link>
                    <span v-else>{{ navn(v) }}</span>
                </h3>

                <div v-for="(t, j) in v.tilbud" :key="j" class="handel">
                    <div class="handel__top">
                        <span :class="badge(t.status)">{{ data.etiketter[t.status] || t.status }}</span>
                        <span class="handel__butik">{{ t.retailer_name }}</span>
                    </div>

                    <p class="handel__pris">
                        {{ kr(t.price) }}
                        <span v-if="procent(t)" class="handel__spar">−{{ procent(t) }} %</span>
                    </p>

                    <dl class="bevis">
                        <div v-if="t.claimed_reference_price">
                            <dt>Butikken oplyser</dt>
                            <dd class="bevis__paastand">{{ kr(t.claimed_reference_price) }}</dd>
                        </div>
                        <div v-if="t.thirty_day_low">
                            <dt>Vi har målt</dt>
                            <dd>{{ kr(t.thirty_day_low) }}</dd>
                        </div>
                        <div>
                            <dt>Måling dækker</dt>
                            <dd>
                                {{ t.coverage_days || 0 }} dage
                                <span v-if="(t.coverage_days || 0) < 30" class="bevis__kort">kortere end perioden</span>
                            </dd>
                        </div>
                    </dl>

                    <!--
                        Kortet var en blindgyde: man kunne læse at et tilbud
                        var godt og så ikke gøre noget ved det. Klikket går
                        gennem vores eget led, så det tæller med.
                    -->
                    <div class="handel__veje">
                        <a v-if="t.link" :href="t.link" class="knap knap--butik" rel="sponsored nofollow">
                            Se hos {{ t.retailer_name }}
                        </a>
                        <Link v-if="v.route_to_productdetail" :href="v.route_to_productdetail" class="knap knap--vare">
                            Alle priser
                        </Link>
                    </div>
                </div>
            </div>
        </article>
    </div>

    <p v-if="varer.length" class="forbehold">
        Tallene er vores egne målinger af varens pris hos den enkelte butik. Vi sammenligner
        butikkens oplyste før-pris med den laveste pris vi selv har registreret i perioden.
    </p>
</template>

<style scoped>
.handel__veje { display: flex; gap: .5rem; flex-wrap: wrap; margin-top: .6rem; }
.knap {
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: var(--pc-radius); padding: .35rem .8rem;
    font-size: .8125rem; font-weight: 600; text-decoration: none;
    border: 1px solid transparent; transition: all var(--pc-transition, .15s);
}
.knap--butik { background: var(--pc-accent-solid); color: var(--pc-on-accent); }
.knap--butik:hover { filter: brightness(1.08); color: var(--pc-on-accent); }
.knap--vare { border-color: var(--pc-border); color: var(--pc-text-primary); }
.knap--vare:hover { border-color: var(--pc-border-hover); color: var(--pc-text-primary); }

.tom, .forbehold { color: var(--pc-text-secondary); font-size: .85rem; margin-bottom: 0; }
.forbehold { margin-top: 1rem; max-width: 70ch; }

.tilbudsgitter { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fill, minmax(19rem, 1fr)); }

.tilbud {
    display: flex; gap: 1rem;
    border: 1px solid var(--pc-border); border-radius: var(--pc-radius-lg);
    background: var(--pc-bg-secondary); padding: 1rem;
}
.tilbud__billede { flex: 0 0 5.5rem; display: block; }
.tilbud__billede img { width: 100%; height: auto; object-fit: contain; background: #fff; border-radius: var(--pc-radius-sm); }
.tilbud__intetbillede { display: block; width: 100%; aspect-ratio: 1; background: var(--pc-bg-tertiary); border-radius: var(--pc-radius-sm); }
.tilbud__krop { min-width: 0; flex: 1; }
.tilbud__navn { font-size: .95rem; line-height: 1.3; margin: 0 0 .5rem; }
.tilbud__navn a { color: var(--pc-text-primary); text-decoration: none; }
.tilbud__navn a:hover { color: var(--pc-accent); }

.handel + .handel { border-top: 1px dashed var(--pc-border); margin-top: .6rem; padding-top: .6rem; }
.handel__top { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; margin-bottom: .25rem; }
.handel__butik { color: var(--pc-text-secondary); font-size: .85rem; }
.handel__pris { font-size: 1.35rem; font-weight: 700; color: var(--pc-price); margin: 0 0 .4rem; }
.handel__spar {
    font-size: .8rem; font-weight: 700; color: var(--pc-on-accent, #0A0C10);
    background: var(--pc-price); border-radius: var(--pc-radius-full);
    padding: .1rem .45rem; margin-left: .35rem; vertical-align: middle;
}

.tag {
    font-size: .72rem; font-weight: 700; letter-spacing: .02em;
    border-radius: var(--pc-radius-full); padding: .12rem .5rem;
    background: var(--pc-bg-tertiary); color: var(--pc-text-secondary);
}
.tag--god { background: var(--pc-success); color: var(--pc-on-accent, #0A0C10); }
.tag--svag { background: var(--pc-warning); color: #0A0C10; }
/*
 * Omridset og ikke fyldt. Hvid paa --pc-danger gav 3,6:1, under WCAG AA
 * for fed smaatekst. Roed tekst paa kortbaggrunden giver 5,0:1 — og et
 * omrids skriger mindre, hvilket passer bedre til en paastand om en
 * navngiven butik.
 */
.tag--daarlig {
    background: transparent;
    color: var(--pc-danger);
    box-shadow: inset 0 0 0 1px var(--pc-danger);
}

.bevis { display: grid; grid-template-columns: 1fr 1fr; gap: .15rem .75rem; margin: 0; font-size: .78rem; }
.bevis dt { color: var(--pc-text-secondary); font-weight: 400; }
.bevis dd { margin: 0 0 .2rem; color: var(--pc-text-primary); font-weight: 600; }
.bevis__paastand { text-decoration: line-through; color: var(--pc-price-old, var(--pc-text-secondary)) !important; }
.bevis__kort { display: block; color: var(--pc-warning); font-weight: 400; font-size: .72rem; }
</style>
